import React, { useCallback, useEffect, useRef, useState } from 'react';
import SaaSBillingService from '../../../services/superadmin/SaaSBillingService';
import './Header.css';

const BODY_CLASS = 'saas-alert-active';
const HEIGHT_VAR = '--saas-alert-h';
// A partir daqui o aviso passa a vermelho e deixa de ser dispensavel: o acesso cai em
// dois dias ou menos, e a essa altura fechar o aviso e esconder o problema do usuario.
const CRITICAL_DAYS = 2;

/** Dias inteiros que faltam ate a data informada. Negativo quando ja passou. */
const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const today = new Date();
  const target = new Date(`${isoDate}T00:00:00`);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target - startOfToday) / 86400000);
};

const formatDate = (isoDate) => new Date(`${isoDate}T00:00:00`).toLocaleDateString('pt-BR');

const SaaSOverdueAlert = () => {
  const [status, setStatus] = useState(null);
  const [dismissedKey, setDismissedKey] = useState(() => sessionStorage.getItem('saas_payment_alert_dismissed'));
  const alertRef = useRef(null);

  useEffect(() => {
    let active = true;
    let loading = false;
    const refresh = async () => {
      if (loading) return;
      loading = true;
      try {
        const response = await SaaSBillingService.currentAccessStatus();
        if (active) setStatus(response.data);
      } catch {
        if (active) setStatus(null);
      } finally {
        loading = false;
      }
    };
    refresh();
    // O pagamento ocorre em outra aba. Também reavaliamos a virada do dia sem reload.
    window.addEventListener('focus', refresh);
    const interval = window.setInterval(refresh, 60000);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
      window.clearInterval(interval);
    };
  }, []);

  // Header, nav-header e sidebar sao position:fixed no tema. Como o alerta tambem
  // e fixo no topo, o layout so desce se publicarmos a altura real dele numa
  // variavel CSS que esses elementos consomem (ver Header.css).
  const publishHeight = useCallback(() => {
    const height = alertRef.current ? alertRef.current.offsetHeight : 0;
    document.body.style.setProperty(HEIGHT_VAR, `${height}px`);
  }, []);

  const overdue = status?.status === 'OVERDUE';
  const dueToday = ['ACTIVE', 'PENDING_PAYMENT'].includes(status?.status) && status?.paymentDueToday;
  const daysToBlock = overdue ? daysUntil(status.blockDate) : null;
  const critical = overdue && daysToBlock !== null && daysToBlock <= CRITICAL_DAYS;
  // Fechar o lembrete de hoje não pode esconder o atraso nem a próxima fatura.
  const dismissalKey = `${overdue ? 'overdue' : 'due-today'}:${status?.nextDueDate}`;
  const visible = (overdue || dueToday) && (critical || dismissedKey !== dismissalKey);

  useEffect(() => {
    if (!visible) {
      // Sem alerta o layout volta ao normal: remove a classe e zera a variavel.
      document.body.classList.remove(BODY_CLASS);
      document.body.style.removeProperty(HEIGHT_VAR);
      return undefined;
    }

    document.body.classList.add(BODY_CLASS);
    publishHeight();

    // O texto quebra em mais linhas no mobile, entao a altura muda com o resize.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publishHeight) : null;
    if (observer && alertRef.current) observer.observe(alertRef.current);
    window.addEventListener('resize', publishHeight);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', publishHeight);
      document.body.classList.remove(BODY_CLASS);
      document.body.style.removeProperty(HEIGHT_VAR);
    };
  }, [visible, publishHeight]);

  // Fecha ate o fim da sessao do navegador.
  const dismiss = () => {
    sessionStorage.setItem('saas_payment_alert_dismissed', dismissalKey);
    setDismissedKey(dismissalKey);
  };

  if (!visible) return null;

  // Sem data de bloqueio conhecida o aviso segue no tom de atencao, sem inventar prazo.
  const message = dueToday
    ? 'Sua fatura vence hoje. Efetue o pagamento para manter sua assinatura em dia.'
    : daysToBlock === null
    ? 'Sua cobrança SaaS está atrasada. Regularize para evitar o bloqueio do acesso.'
    : daysToBlock < 0
      ? 'Sua cobrança SaaS está atrasada e o prazo de carência terminou. O acesso pode ser bloqueado a qualquer momento.'
      : daysToBlock === 0
        ? 'Sua cobrança SaaS está atrasada. O acesso será bloqueado hoje se o pagamento não for identificado.'
        : `Sua cobrança SaaS está atrasada. O acesso será bloqueado em ${daysToBlock} ${daysToBlock === 1 ? 'dia' : 'dias'}, em ${formatDate(status.blockDate)}.`;

  return <div className={`saas-overdue-alert${critical ? ' saas-overdue-alert--critical' : ''}`} role="alert" ref={alertRef}>
    <div><i className="fa-solid fa-triangle-exclamation me-2" /><strong>{critical ? 'Acesso em risco' : dueToday ? 'Vencimento hoje' : 'Pagamento em aberto'}</strong><span className="ms-2">{message}</span></div>
    <div className="d-flex align-items-center gap-2">
      {status.paymentLink && <a className="btn btn-sm btn-primary" href={status.paymentLink} target="_blank" rel="noreferrer">Pagar agora</a>}
      {/* Perto do corte o aviso nao fecha: o usuario precisa ver ate resolver. */}
      {!critical && <button type="button" className="btn-close" aria-label="Fechar" onClick={dismiss} />}
    </div>
  </div>;
};

export default SaaSOverdueAlert;
